
import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Clipboard, ArrowRight, PenTool, User, Phone, MapPin, DollarSign, ShoppingBag, CreditCard, Bike, Loader2, Clock, Store, Truck } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { parseMultipleOrdersText, parseBasicOrderText } from '../services/parserService';
import { Order } from '../types';
import { SmartNumberInput } from './SmartNumberInput';
import { loadGoogleMaps, checkGoogleMapsAvailability } from '../services/googleMapsLoader';

declare var google: any;

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onOrderParsed: (order: Order) => void;
  googleMapsApiKey?: string;
  defaultLat?: number;
  defaultLng?: number;
}

type Tab = 'paste' | 'manual';

export const PasteInputModal: React.FC<Props> = ({ isOpen, onClose, onOrderParsed, googleMapsApiKey, defaultLat, defaultLng }) => {
  const [activeTab, setActiveTab] = useState<Tab>('paste');
  const [text, setText] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);

  // Manual Form State
  const [manualForm, setManualForm] = useState({
      customerName: '',
      phone: '',
      street: '',
      number: '',
      complement: '', 
      neighborhood: '',
      items: '',
      paymentMethod: 'Pix (Cobrar)',
      itemsValue: 0, 
      deliveryFee: 0,
      orderValue: 0,
      lat: 0,
      lng: 0,
      timeLimit: 45, 
      salesChannel: 'Site Próprio',
      type: 'DELIVERY' as 'DELIVERY' | 'TAKEOUT'
  });

  const [deadlineTime, setDeadlineTime] = useState('');

  useEffect(() => {
      if (isOpen) {
          const now = new Date();
          now.setMinutes(now.getMinutes() + 45);
          const hh = String(now.getHours()).padStart(2, '0');
          const mm = String(now.getMinutes()).padStart(2, '0');
          setDeadlineTime(`${hh}:${mm}`);
          setManualForm(prev => ({ ...prev, timeLimit: 45 }));
      }
  }, [isOpen]);

  const [predictions, setPredictions] = useState<any[]>([]);
  const [sessionToken, setSessionToken] = useState<any>(null);
  const [isSearchingAddress, setIsSearchingAddress] = useState(false);

  useEffect(() => {
      if (isOpen && activeTab === 'manual') {
          loadGoogleMaps(googleMapsApiKey).then(() => {
              if (checkGoogleMapsAvailability()) {
                  // Nova arquitetura utiliza tokens de sessão persistentes
                  setSessionToken(new google.maps.places.AutocompleteSessionToken());
              }
          });
      }
  }, [isOpen, activeTab, googleMapsApiKey]);

  useEffect(() => {
      const delayDebounceFn = setTimeout(() => {
          if (manualForm.type === 'DELIVERY' && manualForm.street && sessionToken && manualForm.street.length > 3) {
              if (!manualForm.lat) fetchPredictions(manualForm.street);
          } else { setPredictions([]); }
      }, 500);
      return () => clearTimeout(delayDebounceFn);
  }, [manualForm.street, manualForm.type, sessionToken]);

  const fetchPredictions = async (input: string) => {
      // Verifica se a nova API está disponível
      if (!google.maps.places.AutocompleteSuggestion) {
          console.warn("Nova API de Autocomplete não disponível. Fallback para v1...");
          // Fallback silencioso para o serviço legado se necessário (conforme aviso do Google)
          return;
      }

      setIsSearchingAddress(true);
      try {
          const request = {
              input,
              sessionToken,
              includedRegionCodes: ['BR'],
              locationBias: (defaultLat && defaultLng) ? { center: { lat: defaultLat, lng: defaultLng }, radius: 5000 } : undefined
          };

          const { suggestions } = await google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions(request);
          
          if (suggestions) {
              // Mapeia para o formato legado esperado pelo loop do JSX para evitar refatoração da UI
              const mapped = suggestions.map((s: any) => ({
                  place_id: s.placePrediction.placeId,
                  description: s.placePrediction.text.fullText,
                  structured_formatting: {
                      main_text: s.placePrediction.text.mainText,
                      secondary_text: s.placePrediction.text.secondaryText
                  }
              }));
              setPredictions(mapped);
          } else {
              setPredictions([]);
          }
      } catch (e) {
          console.error("[Autocomplete New API Error]", e);
          setPredictions([]);
      } finally {
          setIsSearchingAddress(false);
      }
  };

  const handlePredictionSelect = async (placeId: string, description: string) => {
      setManualForm(prev => ({ ...prev, street: description }));
      setPredictions([]);
      
      try {
          // Migração para a classe Place (Nova API)
          const { Place } = await google.maps.importLibrary("places");
          const place = new Place({ id: placeId });
          
          // Busca apenas os campos necessários (economia de custos API)
          await place.fetchFields({
              fields: ['location', 'addressComponents', 'displayName']
          });

          if (place.location) {
              let route = '', street_number = '', sublocality = '';
              
              // Mapeamento de componentes (v2 usa camelCase)
              place.addressComponents.forEach((c: any) => {
                  if (c.types.includes('route')) route = c.longText;
                  if (c.types.includes('street_number')) street_number = c.longText;
                  if (c.types.includes('sublocality_level_1') || c.types.includes('sublocality')) sublocality = c.longText;
              });

              setManualForm(prev => ({ 
                  ...prev, 
                  street: route || place.displayName || description, 
                  number: street_number, 
                  neighborhood: sublocality, 
                  lat: place.location.lat(), 
                  lng: place.location.lng() 
              }));

              // Gera novo token para a próxima busca (Best Practice)
              setSessionToken(new google.maps.places.AutocompleteSessionToken());
          }
      } catch (e) {
          console.error("[Place Details New API Error]", e);
          toast.error("Erro ao obter detalhes do endereço.");
      }
  };

  useEffect(() => {
    const total = (manualForm.itemsValue || 0) + (manualForm.deliveryFee || 0);
    setManualForm(prev => ({ ...prev, orderValue: total }));
  }, [manualForm.itemsValue, manualForm.deliveryFee]);

  const handleDeadlineChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const newTime = e.target.value;
      setDeadlineTime(newTime);
      if (newTime) {
          const [hours, minutes] = newTime.split(':').map(Number);
          const now = new Date();
          const target = new Date();
          target.setHours(hours, minutes, 0, 0);
          if (target.getTime() < now.getTime() - 12 * 60 * 60 * 1000) target.setDate(target.getDate() + 1);
          const diffMs = target.getTime() - now.getTime();
          const diffMins = Math.round(diffMs / 60000);
          setManualForm(prev => ({ ...prev, timeLimit: diffMins }));
      }
  };

  const handlePasteSubmit = async () => {
    if (!text.trim()) return;
    setIsProcessing(true);
    try {
      const orders = parseMultipleOrdersText(text);
      if (orders.length > 0) {
        orders.forEach(o => onOrderParsed(o));
        toast.success(orders.length === 1 ? 'Pedido adicionado!' : `${orders.length} pedidos adicionados!`);
        resetAndClose();
      } else {
        toast.error('Não foi possível identificar o formato.');
      }
    } catch (e) { toast.error('Erro ao processar.'); }
    finally { setIsProcessing(false); }
  };

  const handleManualSubmit = () => {
      if (!manualForm.customerName || (manualForm.type === 'DELIVERY' && !manualForm.street)) {
          toast.error('Nome e Endereço são obrigatórios para entrega.');
          return;
      }

      const fullAddress = manualForm.type === 'TAKEOUT' 
        ? 'Retirada em Balcão (Takeout)'
        : `${manualForm.street}, ${manualForm.number}${manualForm.complement ? ` (${manualForm.complement})` : ''} - ${manualForm.neighborhood}`;
      
      const newOrder: Order = {
          id: Math.floor(Math.random() * 9000 + 1000).toString(),
          customerName: manualForm.customerName,
          phone: manualForm.phone,
          address: fullAddress,
          streetAddress: manualForm.type === 'TAKEOUT' ? 'Retirada na Loja' : `${manualForm.street}, ${manualForm.number}`,
          neighborhood: manualForm.type === 'TAKEOUT' ? 'Balcão' : manualForm.neighborhood,
          items: manualForm.items || 'Itens diversos',
          orderValue: manualForm.orderValue,
          deliveryFee: manualForm.type === 'TAKEOUT' ? 0 : manualForm.deliveryFee,
          paymentMethod: manualForm.paymentMethod,
          timeLimit: manualForm.timeLimit,
          createdAt: Date.now(),
          salesChannel: manualForm.salesChannel,
          type: manualForm.type,
          status: 'pending',
          costThirdParty: 0,
          lat: manualForm.lat || (manualForm.type === 'TAKEOUT' ? defaultLat : 0),
          lng: manualForm.lng || (manualForm.type === 'TAKEOUT' ? defaultLng : 0),
          geocodingFailed: manualForm.type === 'DELIVERY' && (!manualForm.lat || !manualForm.lng), 
          isReady: false
      };
      
      onOrderParsed(newOrder);
      resetAndClose();
  };

  const resetAndClose = () => {
      setText('');
      setManualForm({
        customerName: '', phone: '', street: '', number: '', complement: '', neighborhood: '', items: '', paymentMethod: 'Pix (Cobrar)', itemsValue: 0, orderValue: 0, deliveryFee: 0, lat: 0, lng: 0, timeLimit: 45, salesChannel: 'Site Próprio', type: 'DELIVERY'
      });
      setPredictions([]);
      onClose();
  };

  const inputClass = "w-full bg-sal-gunmetal border border-border-dim rounded-lg px-3 py-2 text-base text-text-primary placeholder-slate-500 focus:ring-1 focus:ring-sal-cyber-teal focus:border-sal-cyber-teal outline-none transition-colors";

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose} className="absolute inset-0 bg-sal-gunmetal/80 backdrop-blur-sm" />
        <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }} className="relative bg-sal-gunmetal-light w-full max-w-lg rounded-xl shadow-2xl overflow-visible flex flex-col max-h-[90vh] border border-border-dim">
          <div className="bg-sal-gunmetal text-text-primary px-4 py-3 flex justify-between items-center shrink-0 border-b border-border-dim rounded-t-xl">
            <h3 className="font-bold flex items-center gap-2 text-sal-cyber-teal">
              {activeTab === 'paste' ? <Clipboard className="w-4 h-4"/> : <PenTool className="w-4 h-4"/>}
              Adicionar Pedido
            </h3>
            <button onClick={onClose} className="hover:bg-black/5 dark:hover:bg-white/10 rounded-full p-1 transition-colors" aria-label="Fechar"><X className="w-5 h-5 text-text-muted" /></button>
          </div>
          <div className="flex border-b border-border-dim" role="tablist">
              <button role="tab" aria-selected={activeTab === 'paste'} onClick={() => setActiveTab('paste')} className={`flex-1 py-3 text-base font-bold transition-colors ${activeTab === 'paste' ? 'text-sal-cyber-teal border-b-2 border-sal-cyber-teal bg-black/5 dark:bg-white/5' : 'text-text-muted hover:text-text-primary'}`}>Magic Paste</button>
              <button role="tab" aria-selected={activeTab === 'manual'} onClick={() => setActiveTab('manual')} className={`flex-1 py-3 text-base font-bold transition-colors ${activeTab === 'manual' ? 'text-sal-cyber-teal border-b-2 border-sal-cyber-teal bg-black/5 dark:bg-white/5' : 'text-text-muted hover:text-text-primary'}`}>Manual</button>
          </div>
          <div className="p-4 overflow-y-auto no-scrollbar">
            {activeTab === 'paste' ? (
                <div className="space-y-4">
                    <textarea className="w-full h-48 p-3 bg-sal-gunmetal border border-border-dim rounded-lg text-sm font-mono text-text-primary resize-none" placeholder="Cole o texto aqui..." value={text} onChange={(e) => setText(e.target.value)} />
                </div>
            ) : (
                <div className="space-y-4">
                    {/* Toggle Delivery/Takeout */}
                    <div className="flex gap-2 p-1 bg-sal-gunmetal rounded-xl border border-border-dim">
                        <button onClick={() => setManualForm({...manualForm, type: 'DELIVERY'})} className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-lg text-xs font-black uppercase transition-all ${manualForm.type === 'DELIVERY' ? 'bg-sal-cyber-teal text-sal-gunmetal' : 'text-text-muted'}`}><Truck className="w-3.5 h-3.5" /> Entrega</button>
                        <button onClick={() => setManualForm({...manualForm, type: 'TAKEOUT', deliveryFee: 0})} className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-lg text-xs font-black uppercase transition-all ${manualForm.type === 'TAKEOUT' ? 'bg-amber-500 text-white dark:text-sal-gunmetal' : 'text-text-muted'}`}><ShoppingBag className="w-3.5 h-3.5" /> Retirada</button>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <input name="customerName" autoComplete="name" value={manualForm.customerName} onChange={e => setManualForm({...manualForm, customerName: e.target.value})} className={inputClass} placeholder="Nome do Cliente" />
                        <input name="phone" autoComplete="tel" value={manualForm.phone} onChange={e => setManualForm({...manualForm, phone: e.target.value})} className={inputClass} placeholder="Telefone" />
                    </div>

                    {manualForm.type === 'DELIVERY' && (
                        <div className="space-y-2 bg-sal-gunmetal p-3 rounded-lg border border-border-dim">
                            <div className="flex gap-2 relative">
                                <div className="relative flex-1">
                                    <input value={manualForm.street} onChange={e => setManualForm({...manualForm, street: e.target.value, lat: 0, lng: 0})} className={inputClass} placeholder="Rua..." autoComplete="off" />
                                    {predictions.length > 0 && (
                                        <ul className="absolute left-0 right-0 top-full mt-1 bg-sal-gunmetal border border-border-dim rounded-lg shadow-2xl z-[60] max-h-48 overflow-y-auto">
                                            {predictions.map((p) => (
                                                <li key={p.place_id} onClick={() => handlePredictionSelect(p.place_id, p.description)} className="px-3 py-2 text-sm hover:bg-black/5 dark:hover:bg-white/10 cursor-pointer text-text-secondary">
                                                    <span className="font-bold text-text-primary block">{p.structured_formatting.main_text}</span>
                                                    <span className="text-xs">{p.structured_formatting.secondary_text}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                    {isSearchingAddress && (
                                        <div className="absolute right-3 top-1/2 -translate-y-1/2">
                                            <Loader2 className="w-4 h-4 animate-spin text-sal-cyber-teal" />
                                        </div>
                                    )}
                                </div>
                                <input value={manualForm.number} onChange={e => setManualForm({...manualForm, number: e.target.value})} className={`${inputClass} w-20`} placeholder="Nº" />
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                                <input value={manualForm.complement} onChange={e => setManualForm({...manualForm, complement: e.target.value})} className={inputClass} placeholder="Complemento" />
                                <input value={manualForm.neighborhood} onChange={e => setManualForm({...manualForm, neighborhood: e.target.value})} className={inputClass} placeholder="Bairro" />
                            </div>
                        </div>
                    )}

                    <div className="grid grid-cols-3 gap-3">
                        <input value={manualForm.items} onChange={e => setManualForm({...manualForm, items: e.target.value})} className={inputClass} placeholder="Itens..." />
                        <select value={manualForm.paymentMethod} onChange={e => setManualForm({...manualForm, paymentMethod: e.target.value})} className={inputClass}>
                            <option value="Pix (Cobrar)">Pix</option>
                            <option value="Dinheiro (Cobrar)">Dinheiro</option>
                            <option value="Maquininha (Cobrar)">Cartão</option>
                            <option value="Pago Online">Online</option>
                        </select>
                        <input type="time" value={deadlineTime} onChange={handleDeadlineChange} className={inputClass} />
                    </div>
                </div>
            )}
          </div>
          <div className="p-4 border-t border-border-dim flex justify-end bg-sal-gunmetal rounded-b-xl gap-2">
             <button onClick={resetAndClose} className="px-4 py-2 text-base text-text-muted font-medium">Cancelar</button>
             <button onClick={activeTab === 'paste' ? handlePasteSubmit : handleManualSubmit} className="px-6 py-2 bg-sal-cyber-teal text-white dark:text-sal-gunmetal font-bold rounded-lg shadow-sm">
                {activeTab === 'paste' ? 'Processar' : 'Criar Pedido'}
             </button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};
